import { useInfiniteQuery } from '@tanstack/react-query';
import { Beacon, Device, Gateway } from '@ixicare/resources';
import React from 'react';
import { mapBeaconToUnified, mapDeviceToUnified, mapGatewayToUnified } from '~modules/devices/utils/deviceMapping';
import SiteStore from '~modules/sites/store/Site';
import { Status, UnifiedDBG } from './types';

const PAGE_LIMIT = 50;

export interface DeviceHealthTablesProps {
  site?: SiteStore | null;
}

export const useDeviceHealthTables = ({ site }: DeviceHealthTablesProps) => {
  const deviceQueryFn = ({ pageParam = 1 }: { pageParam?: number }) =>
    Device.getDevices({
      site: site?.uuid,
      page: pageParam,
      perPage: PAGE_LIMIT,
    });

  const beaconQueryFn = ({ pageParam = 1 }: { pageParam?: number }) =>
    Beacon.getBeacons({
      siteId: site?.uuid,
      page: pageParam,
      perPage: PAGE_LIMIT,
    });

  const gatewayQueryFn = ({ pageParam = 1 }: { pageParam?: number }) =>
    Gateway.getGateways({
      siteUuid: site?.uuid,
      page: pageParam,
      perPage: PAGE_LIMIT,
    });

  const archivedDeviceQueryFn = ({ pageParam = 1 }: { pageParam?: number }) =>
    Device.getDevices({
      site: site?.uuid,
      page: pageParam,
      perPage: PAGE_LIMIT,
      isArchived: true,
    });

  const archivedBeaconQueryFn = ({ pageParam = 1 }: { pageParam?: number }) =>
    Beacon.getBeacons({
      siteId: site?.uuid,
      page: pageParam,
      perPage: PAGE_LIMIT,
      isArchived: true,
    });

  const archivedGatewayQueryFn = ({ pageParam = 1 }: { pageParam?: number }) =>
    Gateway.getGateways({
      siteUuid: site?.uuid,
      page: pageParam,
      perPage: PAGE_LIMIT,
      isArchived: true,
    });

  interface combinedQueryFilters {
    siteUuid?: string;
  }

  type ExhaustedAPIs = {
    devices: boolean;
    beacons: boolean;
    gateways: boolean;
    archivedDevices: boolean;
    archivedBeacons: boolean;
    archivedGateways: boolean;
  };

  const makeExhausted = (): ExhaustedAPIs => ({
    devices: false,
    beacons: false,
    gateways: false,
    archivedDevices: false,
    archivedBeacons: false,
    archivedGateways: false,
  });

  const useCombinedDevicesQuery = (filters: combinedQueryFilters) => {
    // Track exhausted APIs per site so stale responses from a previous site
    // can't poison the tracker after the user switches.
    const exhaustedBySite = React.useRef<Record<string, ExhaustedAPIs>>({});

    return useInfiniteQuery({
      queryKey: ['combinedDevices', filters],
      enabled: !!filters.siteUuid,
      initialPageParam: 1,
      queryFn: async ({ pageParam }) => {
        const page = (pageParam as number) ?? 1;
        const siteUuid = filters.siteUuid!;

        if (page === 1 || !exhaustedBySite.current[siteUuid]) {
          exhaustedBySite.current[siteUuid] = makeExhausted();
        }
        const exhausted = exhaustedBySite.current[siteUuid];

        const fetchWithFallback = async <T>(fn: () => Promise<T[]>, apiName: keyof ExhaustedAPIs): Promise<T[]> => {
          if (exhausted[apiName]) {
            return [];
          }

          try {
            const result = await fn();
            if (result.length === 0) {
              exhausted[apiName] = true;
            }
            return result;
          } catch (error: any) {
            if (error?.response?.status === 404 || error?.status === 404) {
              exhausted[apiName] = true;
              return [];
            }
            throw error;
          }
        };

        const [devices, beacons, gateways, archivedDevices, archivedBeacons, archivedGateways] = await Promise.all([
          fetchWithFallback(() => deviceQueryFn({ pageParam: page }), 'devices'),
          fetchWithFallback(() => beaconQueryFn({ pageParam: page }), 'beacons'),
          fetchWithFallback(() => gatewayQueryFn({ pageParam: page }), 'gateways'),
          fetchWithFallback(() => archivedDeviceQueryFn({ pageParam: page }), 'archivedDevices'),
          fetchWithFallback(() => archivedBeaconQueryFn({ pageParam: page }), 'archivedBeacons'),
          fetchWithFallback(() => archivedGatewayQueryFn({ pageParam: page }), 'archivedGateways'),
        ]);

        const devicesMapped: UnifiedDBG[] = devices.map((x) => mapDeviceToUnified(x));
        const gatewaysMapped = gateways.map((x) => mapGatewayToUnified(x));
        const beaconsMapped = beacons.map((x) => mapBeaconToUnified(x));
        const archivedDevicesMapped: UnifiedDBG[] = archivedDevices.map((x) => ({
          ...mapDeviceToUnified(x),
          status: Status.ARCHIVED,
        }));
        const archivedGatewaysMapped = archivedGateways.map((x) => ({
          ...mapGatewayToUnified(x),
          status: Status.ARCHIVED,
        }));
        const archivedBeaconsMapped = archivedBeacons.map((x) => ({
          ...mapBeaconToUnified(x),
          status: Status.ARCHIVED,
        }));
        const combinedData = [
          ...devicesMapped,
          ...gatewaysMapped,
          ...beaconsMapped,
          ...archivedDevicesMapped,
          ...archivedGatewaysMapped,
          ...archivedBeaconsMapped,
        ];

        return {
          data: combinedData,
          currentPage: page,
        };
      },
      getNextPageParam: (lastPage: { data: UnifiedDBG[]; currentPage: number }) => {
        const exhausted = filters.siteUuid ? exhaustedBySite.current[filters.siteUuid] : undefined;
        if (!exhausted) {
          return lastPage.currentPage + 1;
        }

        const allExhausted =
          exhausted.devices &&
          exhausted.beacons &&
          exhausted.gateways &&
          exhausted.archivedDevices &&
          exhausted.archivedBeacons &&
          exhausted.archivedGateways;

        if (allExhausted) {
          return undefined;
        }

        return lastPage.currentPage + 1;
      },
      select: (data: { pages: { data: UnifiedDBG[]; currentPage: number }[]; pageParams: unknown[] }) => ({
        ...data,
        flatData: data.pages.flatMap((page) => page.data),
      }),
    });
  };

  const { data, error, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useCombinedDevicesQuery({
    siteUuid: site?.uuid,
  });

  // Auto-fetch all pages when data is available
  React.useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    data,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};
